/**
 * Who set an aisle break, and when — the store fixtures GET's `aisleBreaks`,
 * shown as the tooltip on a declared break.
 *
 * The page sends the pog alone on Save; the backend stamps a new editor break
 * `declared_ui` with the saving user and time, and keeps the writer of every
 * break whose bay comes back with its GET id. The POST answers with the saved
 * fixtures in the GET's shape, which the page adopts.
 */
import { createContext, useCallback, useContext } from "react";

import type {
  FixturesPogAisleBreak,
  ResponseGetCategories_categoryId_Stores_storeId_FixturesPog,
} from "@CommonApi/categories/_categoryId_/stores/_storeId_/fixturesPog";
import { useTranslation } from "src/i18n";

/** Keyed by the bay the break follows (`bayUniqueId`), which survives every edit of that bay. */
export type AisleBreakProvenanceByBay = ReadonlyMap<string, FixturesPogAisleBreak>;

/** The fixtures adapter: every break the GET (or POST) returned, by its bay. */
export const readAisleBreakProvenance = (
  fixtures: Pick<ResponseGetCategories_categoryId_Stores_storeId_FixturesPog, "aisleBreaks">,
): AisleBreakProvenanceByBay =>
  new Map((fixtures.aisleBreaks ?? []).map((aisleBreak) => [aisleBreak.bayUniqueId, aisleBreak]));

/** Given only on the store fixtures page; everywhere else no break has known provenance. */
export const AisleBreakProvenanceContext = createContext<AisleBreakProvenanceByBay>(new Map());

/** "1 Oct", or "1 Oct 2025" outside the current year. Null for a missing or unreadable date. */
export const formatAisleBreakDate = (
  isoDate: string | null,
  locale: string | undefined,
  now: Date = new Date(),
) => {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString(locale === "es" ? "es-ES" : "en-GB", {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
};

export interface AisleBreakTooltipCopy {
  setBy: (name: string, date: string) => string;
  setInEditor: string;
  fromFixtureFile: string;
}

/**
 * The tooltip on a declared break. A break from the fixture file says so (the
 * backend has no date for it yet). Any other break — set in the editor, set
 * this session, or of unknown writer — names its writer and date when both are
 * known, and otherwise just says the editor set it.
 */
export const describeAisleBreak = ({
  copy,
  date,
  provenance,
}: {
  copy: AisleBreakTooltipCopy;
  date: string | null;
  provenance: FixturesPogAisleBreak | undefined;
}) => {
  if (provenance?.source === "declared_csv") return copy.fromFixtureFile;

  const name = provenance?.declaredByName?.trim();
  return name && date ? copy.setBy(name, date) : copy.setInEditor;
};

/** `(bayId) => tooltip` for the breaks of the pog on screen. */
export const useDescribeAisleBreak = () => {
  const provenanceByBay = useContext(AisleBreakProvenanceContext);
  const { t, locale } = useTranslation("common");

  return useCallback(
    (bayId: string) => {
      const provenance = provenanceByBay.get(bayId);
      return describeAisleBreak({
        copy: {
          fromFixtureFile: t("planogramCanvas.aisleBreak.fromFixtureFile"),
          setBy: (name, date) => t("planogramCanvas.aisleBreak.setBy", { date, name }),
          setInEditor: t("planogramCanvas.aisleBreak.setInEditor"),
        },
        date: formatAisleBreakDate(provenance?.declaredAt ?? null, locale),
        provenance,
      });
    },
    [locale, provenanceByBay, t],
  );
};
